import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { clearSelected } from "../../../redux/slice/selectionSlice";
import {
  useGetBrokerQuery,
  useCreateBrokerMutation,
  useUpdateBrokerMutation,
} from "../../../redux/api";
import { validateEmail, validatePhone } from "../../../utils/validation";
import { PhoneInput } from "../../../components/PhoneInput";
import { Field } from "../../../components/Field";

const ENTITY = "broker";
const LIST_PATH = "/brokers";

type BrokerForm = {
  name: string;
  code: string;
  companyName: string;
  email: string;
  phone: string;
  commissionRate: string;
  referralCode: string;
  addressLine1: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  payoutAccount: string;
  status: string;
  notes: string;
};

const emptyForm: BrokerForm = {
  name: "",
  code: "",
  companyName: "",
  email: "",
  phone: "",
  commissionRate: "0",
  referralCode: "",
  addressLine1: "",
  city: "",
  state: "",
  country: "India",
  postalCode: "",
  payoutAccount: "",
  status: "active",
  notes: "",
};

const n = (v: string): number | undefined =>
  v.trim() === "" ? undefined : Number(v);

const Broker = () => {
  const dispatch = useAppDispatch();
  const selectedId = useAppSelector((s) => s.selection[ENTITY]);
  const isEdit = !!selectedId;

  const { data: existing } = useGetBrokerQuery(selectedId ?? "", {
    skip: !selectedId,
  });

  const [createBroker, { isLoading: creating }] = useCreateBrokerMutation();
  const [updateBroker, { isLoading: updating }] = useUpdateBrokerMutation();

  const [form, setForm] = useState<BrokerForm>(emptyForm);
  const [baseline, setBaseline] = useState<BrokerForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  useEffect(() => {
    if (!existing) return;
    const next: BrokerForm = {
      name: existing.name ?? "",
      code: existing.code ?? "",
      companyName: existing.companyName ?? "",
      email: existing.email ?? "",
      phone: existing.phone ?? "",
      commissionRate:
        existing.commissionRate != null ? String(existing.commissionRate) : "0",
      referralCode: existing.referralCode ?? "",
      addressLine1: existing.addressLine1 ?? "",
      city: existing.city ?? "",
      state: existing.state ?? "",
      country: existing.country ?? "India",
      postalCode: existing.postalCode ?? "",
      payoutAccount: existing.payoutAccount ?? "",
      status: existing.status ?? "active",
      notes: existing.notes ?? "",
    };
    setForm(next);
    setBaseline(next);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const setField = (key: keyof BrokerForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const goBack = () => {
    dispatch(clearSelected(ENTITY));
    dispatch(navigate(LIST_PATH));
  };

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";

    const emailError = validateEmail(form.email, false);
    if (emailError) errors.email = emailError;

    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      companyName: form.companyName.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      commissionRate: n(form.commissionRate),
      referralCode: form.referralCode.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      payoutAccount: form.payoutAccount.trim() || undefined,
      status: form.status,
      notes: form.notes.trim() || undefined,
    };

    try {
      if (isEdit && selectedId) {
        await updateBroker({ id: selectedId, input: payload }).unwrap();
      } else {
        await createBroker(payload).unwrap();
      }
      goBack();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save broker.",
      );
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>{isEdit ? "Edit broker" : "New broker"}</h1>
          <p>Reseller / partner details.</p>
        </div>
        <button type="button" className="btn" onClick={goBack}>
          ← Back to brokers
        </button>
      </div>

      <section className="panel">
        {formError ? <div className="form-error">{formError}</div> : null}

        <div className="form-grid">
          <Field label="Name *" error={fieldErrors.name}>
            <input
              value={form.name}
              onChange={(e) => {
                setField("name", e.target.value);
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }}
            />
          </Field>
          <label className="field">
            Code
            <input
              value={form.code}
              onChange={(e) => setField("code", e.target.value)}
              placeholder="BRK-001"
            />
          </label>
          <label className="field">
            Company
            <input
              value={form.companyName}
              onChange={(e) => setField("companyName", e.target.value)}
            />
          </label>
          <label className="field">
            Commission %
            <input
              type="number"
              value={form.commissionRate}
              onChange={(e) => setField("commissionRate", e.target.value)}
            />
          </label>
          <Field label="Email" error={fieldErrors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => {
                setField("email", e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }}
            />
          </Field>
          <Field label="Phone" error={fieldErrors.phone}>
            <PhoneInput
              value={form.phone}
              onChange={(v) => {
                setField("phone", v);
                setFieldErrors((prev) => ({ ...prev, phone: undefined }));
              }}
            />
          </Field>
          <label className="field">
            Referral code
            <input
              value={form.referralCode}
              onChange={(e) => setField("referralCode", e.target.value)}
            />
          </label>
          <label className="field">
            Status
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label className="field field--full">
            Address line 1
            <input
              value={form.addressLine1}
              onChange={(e) => setField("addressLine1", e.target.value)}
            />
          </label>
          <label className="field">
            City
            <input
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
            />
          </label>
          <label className="field">
            State
            <input
              value={form.state}
              onChange={(e) => setField("state", e.target.value)}
            />
          </label>
          <label className="field">
            Country
            <input
              value={form.country}
              onChange={(e) => setField("country", e.target.value)}
            />
          </label>
          <label className="field">
            Postal code
            <input
              value={form.postalCode}
              onChange={(e) => setField("postalCode", e.target.value)}
            />
          </label>
          <label className="field">
            Payout account
            <input
              value={form.payoutAccount}
              onChange={(e) => setField("payoutAccount", e.target.value)}
              placeholder="Bank / UPI"
            />
          </label>
          <label className="field field--full">
            Notes
            <textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn"
            onClick={goBack}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create broker"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Broker;
